import {
  BOOST,
  INDEED,
  JOVEO,
  POOL,
  TALROO,
  ZIPRECRUITER,
} from '@/types/channels';
import { addDays, endOfDay } from 'date-fns';

import { ChannelFormState, PoolDetails, VonqDetails } from '../types';

export interface ChannelConfigDefaults {
  /** Fixed campaign duration in days (makes the end-date input read-only). */
  duration?: number;
  /** Fixed per-opening budget (makes the budget input read-only). */
  budget?: number;
  /** Minimum per-opening budget (validation error when below this). */
  minBudget?: number;
  /** Minimum campaign duration in days (user cannot set a shorter window). */
  minDuration?: number;
  /** Maximum campaign duration in days (user cannot set a longer window). */
  maxDuration?: number;
}

/**
 * Get the default values for a channel. Setting these values for the channel impacts their channel form:
 * duration - fixes the duration of the campaign. The input is made read only.
 * budget - fixes the per opening budget of the campaign. The input is made read only.
 * minBudget - minimum budget per opening for the channel. Throws a validation error if the budget is less than the minimum budget.
 * minDuration - minimum duration of the campaign. The user is not allowed to set a duration less than the minimum duration.
 * maxDuration - maximum duration of the campaign. The user is not allowed to set a duration greater than the maximum duration.
 */
export const getChannelDefaults = (
  channelSlug: string,
): ChannelConfigDefaults => {
  switch (channelSlug) {
    case ZIPRECRUITER:
      return {
        duration: 30,
        budget: 149,
      };
    case BOOST:
      return {
        duration: 30,
        minBudget: 150,
      };
    case INDEED:
      return {
        minDuration: 7,
      };
    case POOL:
      return {
        minDuration: 2,
      };
    case JOVEO:
      return {
        minBudget: 100,
        minDuration: 15,
      };
    case TALROO:
      return {
        maxDuration: 30,
      };
    default:
      return {
        minBudget: 50,
      };
  }
};

// The full `PoolOpening` shape is filled in by the pool-setup step (audienceId, body, buttons);
export const poolOpeningDefaults = {
  audienceId: null,
  subject: null,
  body: null,
};

interface BuildChannelDefaultsArgs {
  channelSlug: string;
  channelName: string;
  displayTitle: string;
  openingIds: string[];
  unsupportedOpenings: string[];
  /** Opening IDs already selected for another product of the same channel. */
  excludeOpenings?: string[];
  isFree?: boolean;
  vonqDetails?: VonqDetails & { budget: number; duration: number };
}

/**
 * Build the initial `ChannelFormState` for a channel when the user selects it.
 * Opening budgets are seeded for every selected opening that this channel can post to;
 * unsupported openings are excluded from `openingBudgets` and listed in `unsupportedOpenings`.
 *
 * When `channelSlug === 'pool'` the function automatically:
 * - Marks the channel as free with a `null` budget type and `null` end date
 * - Seeds `poolDetails.openingMessages` for every opening (Pool has no unsupported-opening filtering)
 *
 * The caller is responsible for placing the returned state under the right object key in
 * `JobPostingForm.channels` — for Vonq that's `channel.product_id`; for direct channels
 * it's the channel slug.
 */
export function buildChannelDefaults({
  channelSlug,
  channelName,
  displayTitle,
  openingIds,
  unsupportedOpenings,
  excludeOpenings = [],
  isFree = false,
  vonqDetails,
}: BuildChannelDefaultsArgs): ChannelFormState {
  const channelDefaults = getChannelDefaults(channelSlug);

  const fixedOpeningBudget =
    channelDefaults.budget ?? vonqDetails?.budget ?? null;
  const fixedDuration =
    channelDefaults.duration ??
    (vonqDetails?.duration && vonqDetails?.duration > 0
      ? vonqDetails?.duration
      : null);

  const effectiveIsFree = isFree || channelSlug === POOL;

  const openingBudgets = openingIds.reduce<Record<string, number | null>>(
    (acc, openingId) => {
      if (unsupportedOpenings.includes(openingId)) return acc;
      if (excludeOpenings.includes(openingId)) return acc;
      return { ...acc, [openingId]: effectiveIsFree ? 0 : fixedOpeningBudget };
    },
    {},
  );

  const channelBudget = effectiveIsFree
    ? 0
    : fixedOpeningBudget === null
      ? null
      : fixedOpeningBudget * Object.keys(openingBudgets).length;

  const endAt = fixedDuration
    ? endOfDay(addDays(new Date(), fixedDuration)).toISOString()
    : null;

  const poolDetails: PoolDetails | null =
    channelSlug === POOL
      ? {
          campaignType: 'email',
          fromEmail: 'noreply@wxp.fountain.com',
          isNeverEnding: false,
          openingMessages: openingIds.reduce(
            (acc, openingId) => ({ ...acc, [openingId]: poolOpeningDefaults }),
            {},
          ),
        }
      : null;

  return {
    slug: channelSlug,
    name: channelName,
    displayTitle,
    isFree: effectiveIsFree,
    startsAt: new Date().toISOString(),
    endAt,
    budgetType: channelSlug === POOL ? null : 'lifetime',
    budgetAllocation: 'evenly',
    openingBudgets,
    unsupportedOpenings,
    channelBudget,
    totalChannelBudget: effectiveIsFree ? 0 : channelBudget,
    endAtType: channelSlug === INDEED ? 'fixed' : null,
    isExpanded: true,
    isValidated: false,
    vonqDetails: vonqDetails ?? null,
    poolDetails,
  };
}
